/**
 * 持久化缓存工具
 * 使用 localStorage 存储用户信息和配置信息
 * 
 * 缓存策略:
 * - /api/user - 用户信息（登录后缓存，修改个人信息后更新）
 * - /api/settings/configs - 配置信息（首次加载后缓存，修改配置后更新）
 */

const CACHE_KEYS = {
  USER_INFO: 'linksbird_user_info',
  CONFIGS: 'linksbird_configs',
  USERS_LIST: 'linksbird_users_list', // 新增：用户列表缓存
  ROWS_PER_PAGE: 'linksbird_rows_per_page', // 每页条数缓存
  CACHE_VERSION: 'linksbird_cache_version',
};

const CURRENT_VERSION = '1.0.1';

/**
 * 初始化缓存版本检查
 * 如果版本不匹配，清空所有缓存
 */
export const initCacheVersion = () => {
  const cachedVersion = localStorage.getItem(CACHE_KEYS.CACHE_VERSION);
  if (cachedVersion !== CURRENT_VERSION) {
    console.log('[缓存] 检测到缓存版本变化，清空旧缓存');
    clearAllCache();
    localStorage.setItem(CACHE_KEYS.CACHE_VERSION, CURRENT_VERSION);
  }
};

/**
 * 获取用户信息缓存
 */
export const getCachedUserInfo = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.USER_INFO);
    if (cached) {
      const data = JSON.parse(cached);
      console.log('[缓存] 使用缓存的用户信息');
      return data;
    }
  } catch (error) {
    console.error('[缓存] 读取用户信息缓存失败:', error);
  }
  return null;
};

/**
 * 设置用户信息缓存
 */
export const setCachedUserInfo = (userInfo) => {
  try {
    localStorage.setItem(CACHE_KEYS.USER_INFO, JSON.stringify(userInfo));
    console.log('[缓存] 用户信息已缓存');
  } catch (error) {
    console.error('[缓存] 保存用户信息缓存失败:', error);
  }
};

/**
 * 清除用户信息缓存
 */
export const clearCachedUserInfo = () => {
  try {
    localStorage.removeItem(CACHE_KEYS.USER_INFO);
    console.log('[缓存] 用户信息缓存已清除');
  } catch (error) {
    console.error('[缓存] 清除用户信息缓存失败:', error);
  }
};

/**
 * 获取配置信息缓存
 */
export const getCachedConfigs = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.CONFIGS);
    if (cached) {
      const data = JSON.parse(cached);
      console.log('[缓存] 使用缓存的配置信息');
      return data;
    }
  } catch (error) {
    console.error('[缓存] 读取配置信息缓存失败:', error);
  }
  return null;
};

/**
 * 设置配置信息缓存
 */
export const setCachedConfigs = (configs) => {
  try {
    localStorage.setItem(CACHE_KEYS.CONFIGS, JSON.stringify(configs));
    console.log('[缓存] 配置信息已缓存');
  } catch (error) {
    console.error('[缓存] 保存配置信息缓存失败:', error);
  }
};

/**
 * 清除配置信息缓存
 */
export const clearCachedConfigs = () => {
  try {
    localStorage.removeItem(CACHE_KEYS.CONFIGS);
    console.log('[缓存] 配置信息缓存已清除');
  } catch (error) {
    console.error('[缓存] 清除配置信息缓存失败:', error);
  }
};

/**
 * 获取用户列表缓存
 */
export const getCachedUsersList = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.USERS_LIST);
    if (cached) {
      const data = JSON.parse(cached);
      console.log('[缓存] 使用缓存的用户列表');
      return data;
    }
  } catch (error) {
    console.error('[缓存] 读取用户列表缓存失败:', error);
  }
  return null;
};

/**
 * 设置用户列表缓存
 */
export const setCachedUsersList = (usersList) => {
  try {
    localStorage.setItem(CACHE_KEYS.USERS_LIST, JSON.stringify(usersList));
    console.log('[缓存] 用户列表已缓存');
  } catch (error) {
    console.error('[缓存] 保存用户列表缓存失败:', error);
  }
};

/**
 * 清除用户列表缓存
 */
export const clearCachedUsersList = () => {
  try {
    localStorage.removeItem(CACHE_KEYS.USERS_LIST);
    console.log('[缓存] 用户列表缓存已清除');
  } catch (error) {
    console.error('[缓存] 清除用户列表缓存失败:', error);
  }
};

/**
 * 获取每页条数缓存
 */
export const getCachedRowsPerPage = () => {
  try {
    const cached = localStorage.getItem(CACHE_KEYS.ROWS_PER_PAGE);
    if (cached) {
      const value = parseInt(cached, 10);
      if (!isNaN(value) && value > 0) {
        console.log('[缓存] 使用缓存的每页条数:', value);
        return value;
      }
    }
  } catch (error) {
    console.error('[缓存] 读取每页条数缓存失败:', error);
  }
  return null;
};

/**
 * 设置每页条数缓存
 */
export const setCachedRowsPerPage = (rowsPerPage) => {
  try {
    localStorage.setItem(CACHE_KEYS.ROWS_PER_PAGE, rowsPerPage.toString());
    console.log('[缓存] 每页条数已缓存:', rowsPerPage);
  } catch (error) {
    console.error('[缓存] 保存每页条数缓存失败:', error);
  }
};

/**
 * 清除每页条数缓存
 */
export const clearCachedRowsPerPage = () => {
  try {
    localStorage.removeItem(CACHE_KEYS.ROWS_PER_PAGE);
    console.log('[缓存] 每页条数缓存已清除');
  } catch (error) {
    console.error('[缓存] 清除每页条数缓存失败:', error);
  }
};

/**
 * 清除所有缓存
 */
export const clearAllCache = () => {
  clearCachedUserInfo();
  clearCachedConfigs();
  clearCachedUsersList();
  clearCachedRowsPerPage();
  console.log('[缓存] 所有缓存已清除');
};

/**
 * 获取缓存统计信息
 */
export const getCacheStats = () => {
  const userInfo = getCachedUserInfo();
  const configs = getCachedConfigs();
  const usersList = getCachedUsersList();
  
  return {
    userInfoCached: !!userInfo,
    configsCached: !!configs,
    usersListCached: !!usersList,
    userInfoSize: userInfo ? JSON.stringify(userInfo).length : 0,
    configsSize: configs ? JSON.stringify(configs).length : 0,
    usersListSize: usersList ? JSON.stringify(usersList).length : 0,
  };
};

// 初始化时检查缓存版本
initCacheVersion();
